<template>
  <div class="bbp-list-projects">
    <ListOfProjectsHeader
      :fullInfo="fullInfo"
      :isHeader="isHeader"
    />
    <div class="bbp-list-projects-header">
      <BBPTabsType2
        :tabs="tabs"
        :activeTab.sync="currentTab"
        minmax="120px, 160px"
      />
      <button @mouseenter="showTooltip"
              @mouseleave="hideTooltip"
              class="bbp-button bbp-list-projects-header__button"
              @click.stop="openProjectForm = true">
        <i class="bbp-icon-plus bbp-list-projects-header__icon"/>
        <transition name="bbp-bounce">
          <BBPTooltip :textToolTip="textToolTip" v-if="isShowTooltip"/>
        </transition>
      </button>
      <BBPSearch @change="getSearchValue" :value="searchWords"/>
      <i class="bbp-icon-menu bbp-list-projects-header-menu"
         @click="openPopup = true"
         v-click-outside="closePopup">
        <transition name="bbp-bounce">
          <div class="bbp-list-projects-header-menu-popup"
               v-if="openPopup"
               @mouseleave="closePopup">
            <div class="bbp-list-projects-header-menu-popup__item"
                 @click.stop="resetTable">
              Reset filters
            </div>
          </div>
        </transition>
      </i>
      <div class="bbp-list-projects-header-question"
           @click.stop="openQuestionForm = true"
      >
        <BBPIconHelper/>
      </div>
    </div>
    <div class="bbp-list-projects-body">
      <BBPTable
        :columns="columns"
        :loaderRun="loaderRun"
        :rows="projectsList"
        :no-data-text="noDataText"
        :limit="defLimit"
        :page="currentPage"
        :no-data="noData"
        :resetFilter="resetFilter"
        :resetFilterCalendar="resetFilterCalendar"
        :max-height="'calc(100vh - 280px)'"
        @sort="setSort"
        @filter="setFilter"
        @updateProject="updateProject"
        @onChangeFilter="onChangeFilter"
        @update="updateProjectStatus"
        @delete="openConfirmDelete"
        @copy="openCopyProject"
        @edit="openEditProject"
        @onResetFilter="onResetFilter"
      />
    </div>
    <BBPPagination v-show="projectsList.length > 0 || fullInfo.totalPages > 1"
                   :data="fullInfo"
                   :getQuery="getProjects"
                   :limit="defLimit"
                   :sticky="true"
                   class="bbp-pagination"/>
    <transition name="bbp-fade" appear>
      <BBPLoader v-if="loaderRun"></BBPLoader>
    </transition>
    <transition name="bbp-fade">
      <BBPModalWindow v-if="openCopyUpdates" @close="closeCopyUpdates">
        <BBPCopyUpdates :projects="activeProjectList"
                        :targetProject="copyUpdates"
                        @close="closeCopyUpdates"/>
      </BBPModalWindow>
    </transition>
    <transition name="bbp-fade">
      <BBPModalWindow v-if="openProjectForm" @close="closeProjectForm">
        <BBPProjectForm
          :project="editableProject"
          @updateFullInfo="updateFullInfo"
          @close="closeProjectForm"
          @loader="setLoader"
        />
      </BBPModalWindow>
    </transition>
    <transition name="bbp-fade">
      <BBPDoubleDelete v-if="openDoubleDelete"
                       :id="currentProject.id"
                       :title="'Delete Project: ' + (currentProject.name || 'Project')"
                       @close="closeConfirmDelete"
                       @delete="deleteProject"/>
    </transition>
    <transition name="bbp-fade">
      <BBPModalWindow v-if="openQuestionForm"
                      :album="true"
                      @close="closeQuestionForm"
      >
        <BBPQuestion @close="closeQuestionForm"/>
      </BBPModalWindow>
    </transition>
    <transition name="bbp-fade">
      <BBPModalWindow v-if="openProforma" @close="closeProforma">
        <BBPProforma @close="closeProforma" :invoice="invoice"/>
      </BBPModalWindow>
    </transition>
    <transition name="bbp-fade">
      <BBPModalWindow v-if="openStatusModal" @close="closeStatusModal">
        <RequestChangeStatus
          :isLostOptions="true"
          :options.sync="lostOptions"
          :statusId="2"
          title="State the reason why project has been lost:"
          @save="saveStatusRequest"
          @close="closeStatusModal"
        />
      </BBPModalWindow>
    </transition>
    <transition name="bbp-fade">
      <BBPModalWindow v-if="openStepForm" @close="closeStepForm">
        <ProjectUpdateForm
          :hideSubProject="hideSubProject"
          :default-body="editableStep"
          :project="project"
          :clientProjects="projectsList"
          :isListOFProject="true"
          @close="closeStepForm"/>
      </BBPModalWindow>
    </transition>
  </div>
</template>

<script>

import BBPSearch from '@/components/Main/InputFields/BBPSearch/BBPSearch'
import BBPModalWindow from '@/components/Main/BBPModalWindow/BBPModalWindow'
import BBPIconHelper from '@/components/Main/BBPIcons/BBPIconHelper'
import BBPTooltip from '@/components/Main/BBPTooltip/BBPTooltip'
import BBPCopyUpdates from '@/components/Projects/ListOfProjects/BBPCopyUpdates'
import BBPProjectForm from '@/components/Projects/ListOfProjects/BBPProjectForm'
import BBPTable from '@/components/Main/BBPTable/BBPTable'
import listOfProjectColumns from '@/constants/TablesSettings/listOfProjectColumns'
import ProjectsApi from '@/api/Projects/api'
import {find, findIndex, get, set, cloneDeep} from 'lodash'
import BBPPagination from '@/components/Main/BBPPagination/BBPPagination'
import setProjectFullInfo from '@/mixin/setProjectFullInfo'
import BBPLoader from '@/components/Main/BBPLoader/BBPLoader'
import BBPDoubleDelete from '@/components/Main/BBPDoubleDelete/BBPDoubleDelete'
import ClientApi from '@/api/Clients/api'
import UserApi from '@/api/Users/api'
import TypeServiceProjectApi from '@/api/TypeServiceProject/api'
import BBPProforma from '@/components/Main/BBPProforma/BBPProforma'
import InvoiceApi from '@/api/Invoice/api'
import tabs from '@/constants/tabs'
import {
  COMPLETED_PROJECT,
  IN_PROGRESS_PROJECT,
  LOST_PROJECT,
  SEMI_COMPLETED_PROJECT,
  DELETE_STATUS,
  PENDING_PROJECT,
  NOT_PAID
} from '@/constants/projectStatuses'
import {departmentCategory} from '@/constants/departmentCategory'
import setProjectBody from '@/mixin/setProjectBody'
import filterByDepartment from '@/mixin/filterByDepartment'
import RequestChangeStatus from '@/components/Requests/RequestChangeStatus/RequestChangeStatus'
import {HOURLY, REGULAR, SIDE_HOURLY} from '@/constants/projectTypes'
import ProjectUpdateForm from '@/components/Projects/ProjectCard/ProjectUpdateForm/ProjectUpdateForm'
import BBPQuestion from '../../Main/BBPQuestion/BBPQuestion'
import {
  LOST_OPTIONS,
  LOST_OPTIONS_DUPLICATE_ID,
  LOST_OPTIONS_OTHER_ID
} from '@/constants/lostOptions'
import ListOfProjectsHeader from './ListOfProjectsHeader'
import ProjectsListTags from '@/api/ProjectsListTags/api'
import queryString from 'query-string'
import BBPTabsType2 from '@/components/Main/BBPTabsType2/BBPTabsType2'

export default {
  name: 'ListOfProjects',
  components: {
    BBPTabsType2,
    ListOfProjectsHeader,
    BBPQuestion,
    ProjectUpdateForm,
    BBPProforma,
    BBPPagination,
    BBPIconHelper,
    BBPCopyUpdates,
    BBPTable,
    BBPTooltip,
    BBPProjectForm,
    BBPModalWindow,
    BBPSearch,
    BBPLoader,
    BBPDoubleDelete,
    RequestChangeStatus
  },
  mixins: [setProjectFullInfo, setProjectBody, filterByDepartment],
  data () {
    return {
      tabs,
      dateOpenProforma: Date.now(),
      loaderRun: false,
      openProjectForm: false,
      openCopyUpdates: false,
      searchWords: '',
      openQuestionForm: false,
      noDataText: 'Sorry, no projects found.',
      noData: false,
      defLimit: 30,
      currentPage: 1,
      fullInfo: {},
      sort: '',
      editableProject: {},
      copyUpdates: {},
      currentProject: {},
      invoice: {},
      openDoubleDelete: false,
      openStatusModal: false,
      textToolTip: 'Add project',
      openProforma: false,
      lostOptions: LOST_OPTIONS,
      openStepForm: false,
      editableStep: {},
      project: {},
      hideSubProject: false,
      openPopup: false,
      isShowTooltip: false,
      filterByHashtag: [],
      contactPms: [],
      supportManagers: [],
      resetFilter: 0,
      resetFilterCalendar: 0,
      isFirstLoader: true
    }
  },
  computed: {
    isHeader () {
      return this.currentTab === IN_PROGRESS_PROJECT.id ||
        this.currentTab === PENDING_PROJECT.id ||
        this.currentTab === NOT_PAID.id
    },
    columns () {
      const columns = cloneDeep(listOfProjectColumns).map(col => {
        if (col.action === 'openInvoice') col.action = this.openInvoice
        return col
      })

      if (this.currentTab === LOST_PROJECT.id || this.currentTab === NOT_PAID.id) {
        const noMenu = columns.filter(col => !col.showSemiCompleted && !col.hideProgress && !col.showDeleted)
        noMenu.forEach((el) => {
          if (this.currentTab === NOT_PAID.id) return
          if (el !== undefined) el.isNoClick = true
        })
        return noMenu
      }

      if (this.currentTab === COMPLETED_PROJECT.id || this.currentTab === SEMI_COMPLETED_PROJECT.id) {
        return columns.filter(col => !col.showSemiCompleted && !col.showDeletedOn)
      }

      if (this.currentTab === PENDING_PROJECT.id) {
        const currentColumn = columns.find((el) => el.prop === 'estStartDate')
        currentColumn.hideHead = false
      }
      return columns.filter(col => !col.hideProgress && !col.showDeletedOn)
    },
    currentSort () {
      if (this.currentTab === IN_PROGRESS_PROJECT.id && !this.searchWords) {
        return 'rejectedDate' + '&sort[]=-1'
      }
      if (this.currentTab === COMPLETED_PROJECT.id || this.currentTab === SEMI_COMPLETED_PROJECT.id) {
        return 'approvedDate' + '&sort[]=-1'
      }
      return 'dateEnd' + '&sort[]=-1'
    },
    queryProjects: {
      get () {
        return this.$store.getters.queryProjects
      },
      set (data) {
        this.$store.dispatch('setQueryProjects', data)
      }
    },
    projectsList: {
      get () {
        return this.$store.getters.projectsList
      },
      set (data) {
        this.$store.dispatch('setProjectsList', data)
      }
    },
    tags: {
      get () {
        return this.$store.getters.tags
      },
      set (data) {
        this.$store.dispatch('setTags', data)
      }
    },
    typeServiceList: {
      get () {
        return this.$store.getters.typeServiceList
      },
      set (data) {
        this.$store.dispatch('setTypeServiceList', data)
      }
    },
    activeProjectList: {
      get () {
        return this.$store.getters.projectsList.filter(item => !item.isRemoveInvoice)
      }
    },
    currentTab: {
      get () {
        return this.$store.getters.currentTab
      },
      set (data) {
        this.$store.dispatch('setCurrentTab', data)
      }
    },
    clientsListProjects: {
      get () {
        return this.$store.getters.clientsListProjects
      },
      set (data) {
        this.$store.dispatch('setClientsListProjects', data)
      }
    },
    usersListProjects: {
      get () {
        return this.$store.getters.usersListProjects
      },
      set (data) {
        this.$store.dispatch('setUsersListProjects', data)
      }
    }
  },
  methods: {
    async getTags () {
      try {
        const { data } = await ProjectsListTags.getTags()
        this.tags = data
      } catch (err) {
        console.error(err)
      }
    },
    closeStatusModal () {
      this.openStatusModal = false
      const editableRow = find(this.projectsList, {id: this.currentProject.id}) || {}
      editableRow.status = this.currentProject.status
      this.currentProject = {}
    },
    saveStatusRequest (comment) {
      const item = comment.find(({id}) => id === LOST_OPTIONS_DUPLICATE_ID || id === LOST_OPTIONS_OTHER_ID)
      if (comment) set(this.currentProject, 'comments', comment.map(({text}) => text))
      set(this.currentProject, 'status', item ? DELETE_STATUS.id : LOST_PROJECT.id)
      this.updateProjectStatus({row: this.currentProject, value: this.currentProject.status})
      this.closeStatusModal()
    },
    openInvoice (row) {
      const invoiceId = get(row, 'invoiceId.id', '')
      if (!invoiceId) return
      this.loaderRun = true
      this.invoice = {}
      InvoiceApi.getInvoiceById(invoiceId)
        .then(({data}) => {
          this.dateOpenProforma = Date.now()
          this.openProforma = true
          this.loaderRun = false
          this.invoice = data
        })
        .catch(err => {
          this.loaderRun = false
          this.invoice = {}
          console.error(err)
        })
    },
    closeProjectForm () {
      this.editableProject = {}
      this.openProjectForm = false
    },
    closeCopyUpdates () {
      this.copyUpdates = {}
      this.openCopyUpdates = false
    },
    closeQuestionForm () {
      this.openQuestionForm = false
    },
    closeProforma () {
      const time = Date.now() - this.dateOpenProforma
      if (time < 500) return
      this.openProforma = false
      this.invoice = {}
    },
    setLoader (val) {
      this.loaderRun = val
    },
    openConfirmDelete ({row}) {
      this.currentProject = row
      const { projectType, id } = row
      const isSubproject = projectType.id === SIDE_HOURLY.id
      if (isSubproject) {
        const { totalDocs } = this.fullInfo
        this.fullInfo = {
          ...this.fullInfo,
          totalDocs: totalDocs - 1
        }
        this.deleteProject(id)
      } else {
        this.openStatusModal = true
      }
    },
    closeConfirmDelete () {
      this.currentProject = {}
      this.openDoubleDelete = false
    },
    onChangeFilter (column, item) {
      const { prop } = column
      const { _id, isActive } = item
      if (prop === 'contactPm') {
        this.queryProjects.contactPm = isActive ? _id : undefined
        column.activeFilter = isActive
      } else if (prop === 'supportManager') {
        this.queryProjects.supportManager = isActive ? _id : undefined
        column.activeFilter = isActive
      } else {
        const value = column[prop]
        if (!value) this.resetFilterCalendar = this.resetFilterCalendar + 1
        const { end, start } = value
        const queryDate = value ? `${start.toISOString()};;${end.toISOString()}` : undefined
        if (queryDate === this.queryProjects[prop]) return
        this.queryProjects[prop] = queryDate
      }
      this.currentPage = 1
      this.setQuery()
      this.getProjects({})
    },
    setQuery () {
      const {
        contactPm,
        supportManager,
        search,
        estCompletion,
        estStartDate,
        currentTab,
        page
      } = this.queryProjects

      const obj = {
        contactPm: contactPm || undefined,
        supportManager: supportManager || undefined,
        search: search || undefined,
        estCompletion: estCompletion || undefined,
        estStartDate: estStartDate || undefined,
        currentTab,
        page
      }
      this.$router.replace({query: obj})
    },
    updateProject (currentElem) {
      const {
        row,
        column,
        isUpdate,
        isUpdateStepServiceProject,
        value
      } = currentElem
      if (isUpdate) {
        const { prop } = column
        const newRow = {
          ...row,
          [prop]: value
        }
        this.editProject(newRow, value, prop)
      } else if (isUpdateStepServiceProject) {
        this.editProject(row)
      } else {
        this.project = row
        this.openStepForm = true
      }
    },
    updateServiceProject (data) {
      const { _id, stepServiceProject, typeServiceProject, totalPercent } = data
      const currentItem = this.projectsList.find((el) => el._id === _id)
      currentItem.stepServiceProject = stepServiceProject
      currentItem.typeServiceProject = typeServiceProject
      currentItem.totalPercent = totalPercent
    },
    async editProject (row, value, prop) {
      const project = this.setProjectBody(row)
      try {
        const { data } = await ProjectsApi.editProject(project)
        this.updateServiceProject(data)

        if (!value && (prop === 'contactPm' || prop === 'supportManager')) this.getUsersFilter()
      } catch (err) {
        console.error(err)
      }
    },
    closeStepForm () {
      this.openStepForm = false
    },
    updateTotalValue ({ amount }, isNew) {
      const { totalAmount, totalDocs } = this.fullInfo
      this.fullInfo = {
        ...this.fullInfo,
        totalDocs: isNew ? totalDocs + 1 : totalDocs - 1,
        totalAmount: isNew ? totalAmount + amount : totalAmount - amount
      }
    },
    updateFullInfo (project) {
      this.updateTotalValue(project, true)
    },
    updateProjectStatus ({row, column = {}, value}) {
      row.status = value
      this.updateTotalValue(row)
      const project = this.setProjectBody(row)
      if (value === COMPLETED_PROJECT.id || value === SEMI_COMPLETED_PROJECT.id) {
        this.completeProject(row, value)
        return
      }
      ProjectsApi.editProject(project)
        .then(({ data }) => {
          this.filterAutoStatus(data, row, value)
        })
        .catch(err => {
          console.error(err)
        })
    },
    completeProject (row, newStatus) {
      ProjectsApi.completeProject(row.id, newStatus)
        .then(({ data }) => {
          this.filterAutoStatus(data, row, newStatus)
        })
        .catch(err => {
          console.error(err)
        })
    },
    filterAutoStatus (data, row, value) {
      const { projectType, childrenProjects = [] } = row
      if (projectType.id === HOURLY.id) {
        const items = this.projectsList.filter((el) => {
          const item = childrenProjects.find((c) => c._id === el._id)
          if (item === undefined) return true
          return !item
        })
        this.projectsList = items

        const { totalDocs } = this.fullInfo
        this.fullInfo = {
          ...this.fullInfo,
          totalDocs: totalDocs - childrenProjects.length
        }
      }
      this.projectsList = this.projectsList.filter(p => p.id !== data.id)
      set(row, 'status', value)
    },
    deleteProject (id) {
      const index = findIndex(this.projectsList, {id: id})
      const oldElem = index !== -1 ? this.projectsList.splice(index, 1) : ''
      ProjectsApi.deleteProject(id)
        .then(() => {
          this.closeConfirmDelete()
        })
        .catch((err) => {
          if (oldElem) {
            this.projectsList.splice(index, 0, ...oldElem)
          }
          this.closeConfirmDelete()
          console.error(err)
        })
    },
    openEditProject ({row}) {
      this.openProjectForm = true
      this.editableProject = row
    },
    openCopyProject ({row}) {
      this.openCopyUpdates = true
      this.copyUpdates = row
    },
    getSearchValue (val) {
      this.currentPage = 1
      this.searchWords = val
      if (this.queryProjects.search !== val) {
        this.queryProjects.search = val
        this.setQuery()
      }
      this.getProjects({})
    },
    getProjectValue (val) {
      this.searchWords = val
      if (this.queryProjects.search !== val) {
        this.queryProjects.search = val
        this.setQuery()
      }
    },
    filterByTag (newVal) {
      const item = `tags[]=${[newVal]}`
      const currentIndex = this.filterByHashtag.findIndex((el) => el === item)
      if (currentIndex === -1) {
        this.filterByHashtag.push(item)
      } else {
        this.filterByHashtag.splice(currentIndex, 1)
      }
      this.currentPage = 1
      this.getProjects({})
    },
    setFilter ({value, newVal}) {
      if (newVal) return this.filterByTag(newVal)
    },
    getProjects ({page, sort}) {
      const sortedBy = sort || this.currentSort
      this.loaderRun = true
      this.currentPage = page || this.currentPage
      if (page) {
        this.queryProjects.page = page
        this.setQuery()
      }
      const {
        search,
        estCompletion,
        estStartDate,
        manager: projectManager,
        supportManager,
        contactPm
      } = this.queryProjects

      ProjectsApi.getProjects({
        page: this.currentPage,
        limit: this.defLimit,
        sort: sortedBy,
        status: this.currentTab,
        search,
        estCompletion,
        estStartDate,
        projectManager,
        supportManager,
        contactPm,
        projectType: `${REGULAR.id}&projectType[]=${HOURLY.id}&projectType[]=${SIDE_HOURLY.id}`,
        tags: this.filterByHashtag
      })
        .then(async (resp) => {
          await this.getUsersListSelect()
          this.fullInfo = resp.data
          this.projectsList = await this.getProjectInfo(resp.data.projects)
          if (this.projectsList.length === 0) this.noData = true
          this.isFirstLoader = false
        })
        .catch((err) => {
          this.loaderRun = false
          this.isFirstLoader = false
          console.error(err)
        })
    },
    async getProjectInfo (projects) {
      const promises = projects.map(async project => {
        const result = await this.setProjectFullInfo(project)
        return result
      })
      const result = await Promise.all(promises)
      return result
    },
    setSort (value) {
      this.sort = value
      this.getProjects({sort: this.sort})
    },
    async getClientsSelect () {
      try {
        const { data } = await ClientApi.getClientsSelect({contactType: 'contactType[]=1'})
        this.clientsListProjects = data
      } catch (err) {
        console.error(err)
      }
    },
    async getTypeServiceProject () {
      try {
        const { data } = await TypeServiceProjectApi.getTypeServiceProject()
        this.typeServiceList = data
      } catch (err) {
        console.error(err)
      }
    },
    async getUsersListSelect () {
      if (this.usersListProjects.length) {
        this.setUsersInputs()
        this.loaderRun = false
        return
      }

      try {
        const { data } = await UserApi.getUsersListSelect({})
        this.usersListProjects = data
        this.setUsersInputs()
        this.loaderRun = false
      } catch (err) {
        this.loaderRun = false
        console.error(err)
      }
    },
    setUsersInputs () {
      const { PROJECT_MANAGER } = departmentCategory
      const contactPm = this.columns.find((el) => el.prop === 'contactPm')
      const supportManager = this.columns.find((el) => el.prop === 'supportManager')

      contactPm.items = this.sortUsersByDepartment(this.usersListProjects, PROJECT_MANAGER)
      supportManager.items = this.sortUsersByDepartment(this.usersListProjects, PROJECT_MANAGER)
    },
    onResetFilter () {
      this.resetFilter = this.resetFilter + 1
    },
    setEstCompletion () {
      const { estCompletion } = this.queryProjects
      if (!estCompletion) return
      const [start, end] = estCompletion.split(';;')
      const item = this.columns.find((el) => el.prop === 'estCompletion')
      item.estCompletion = {
        end: new Date(end),
        start: new Date(start)
      }
      item.isActive = true
    },
    setEstStartDate () {
      const { estStartDate } = this.queryProjects
      if (!estStartDate) return
      const [start, end] = estStartDate.split(';;')
      const item = this.columns.find((el) => el.prop === 'estStartDate')
      item.estCompletion = {
        end: new Date(end),
        start: new Date(start)
      }
      item.isActive = true
    },
    async getUsersFilter () {
      try {
        const { data } = await ProjectsApi.getUsersFilter({ status: this.currentTab })
        const { contactPms = [], supportManagers = [] } = data
        this.resetFilter = this.resetFilter + 1
        const contactPm = this.columns.find((el) => el.prop === 'contactPm')
        contactPm.options = contactPms
        const supportManager = this.columns.find((el) => el.prop === 'supportManager')
        supportManager.options = supportManagers
        this.updateFilter(contactPms, supportManagers, supportManager, contactPm)
      } catch (err) {
        console.error(err)
      }
    },
    updateFilter (contactPms, supportManagers, supportManagerColumn, contactPmColumn) {
      const { contactPm, supportManager } = this.queryProjects
      const currentPm = contactPms.find((el) => el._id === contactPm)
      const currentSm = supportManagers.find((el) => el._id === supportManager)

      if (currentPm) currentPm.isActive = true
      contactPmColumn.activeFilter = !!currentPm
      if (currentSm) currentSm.isActive = true
      supportManagerColumn.activeFilter = !!currentSm

      if (!currentPm && contactPm) {
        this.currentPage = 1
        this.queryProjects.contactPm = ''
        this.getProjects({})
      }
      if (!currentSm && supportManager) {
        this.currentPage = 1
        this.queryProjects.supportManager = ''
        this.getProjects({})
      }
    },
    setDefaultQuery () {
      const { location } = window
      const { search } = location
      if (!search) {
        this.setQuery()
        return
      }
      this.queryProjects = {
        ...queryString.parse(search)
      }
    },
    closePopup () {
      this.openPopup = false
    },
    showTooltip () {
      this.isShowTooltip = true
    },
    hideTooltip () {
      this.isShowTooltip = false
    },
    resetColumns () {
      this.resetFilter = this.resetFilter + 1
      this.resetFilterCalendar = this.resetFilterCalendar + 1
      const contactPm = this.columns.find((el) => el.prop === 'contactPm')
      const { options = [] } = contactPm
      options.forEach((el) => {
        el.isActive = false
      })
      contactPm.activeFilter = false
      const supportManager = this.columns.find((el) => el.prop === 'supportManager')
      const { options: optionsSupportManager = [] } = supportManager
      optionsSupportManager.forEach((el) => {
        el.isActive = false
      })
      supportManager.activeFilter = false
      const item = this.columns.find((el) => el.prop === 'estCompletion')
      item.estCompletion = ''
      item.activeFilter = false
    },
    resetTable () {
      if (!this.isFirstLoader) this.currentPage = 1
      this.resetColumns()
      this.searchWords = ''
      this.queryProjects = {
        currentTab: this.currentTab,
        page: this.isFirstLoader ? this.currentPage : 1
      }
      this.$router.replace({query: this.queryProjects}).catch(err => err)
      this.getProjects({})
    },
    closeModalTimer () {
      this.$store.dispatch('setShowTimerModal', false)
    }
  },
  watch: {
    currentTab () {
      this.resetTable()
      this.getUsersFilter()
      this.closeModalTimer()
    }
  },
  beforeMount () {
    this.setDefaultQuery()
    const { search = '', currentTab, page } = this.queryProjects
    this.searchWords = search
    this.currentTab = Number(currentTab)
    this.currentPage = Number(page)
    if (this.currentTab === IN_PROGRESS_PROJECT.id) this.getProjects({})
    this.setEstCompletion()
    this.setEstStartDate()
    this.getUsersFilter()
    if (!this.clientsListProjects.length) this.getClientsSelect()
    if (!this.tags.length) this.getTags()
    if (!this.typeServiceList.length) this.getTypeServiceProject()
  },
  beforeDestroy () {
    this.typeServiceList = []
  }
}
</script>

<style lang="scss" scoped>
@import "../../../sass/variables";
@import "../../../sass/mixins";

.bbp-list-projects {
  @include global-font;
  background-color: $color-wild-sand;
  height: 100%;

  &-header {
    display: grid;
    grid-template-columns: 1fr auto minmax(200px, 331px) 24px auto;
    align-items: center;
    grid-gap: 8px;
    margin: 5px 0 0 0;

    &__button {
      display: flex;
      justify-content: center;
      align-items: center;
      white-space: nowrap;
      width: 42px;
      height: 42px;
      border-radius: 50%;
      margin-left: 20px;
    }

    &__icon {
      font-size: 18px;
    }

    &-question {
      cursor: pointer;
      display: flex;
      align-items: center;
      opacity: 1;
      transition: .3s linear;

      &:hover {
        opacity: 0.8;
        transition: .3s linear;
      }
    }

    &-menu {
      @include global-font;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 30px;
      font-size: 14px;
      color: $color-accent;
      transition: 0.3s;
      border-radius: 8px;
      background-color: transparent;
      cursor: pointer;
      user-select: none;

      &:before {
        transform: rotate(90deg);
      }

      &:hover {
        color: $color-accent-hover;
        background-color: rgba($color-link-water, 0.4);
      }

      &-popup {
        position: absolute;
        bottom: -40px;
        right: 0;
        max-height: 68px;
        min-width: 92px;
        max-width: 92px;
        background: $color-white;
        box-shadow: 0 0 15px rgba($color-black, 0.12);
        border-radius: 4px;
        z-index: 5;

        &__item {
          font-weight: normal;
          font-size: 12px;
          line-height: 14px;
          text-align: center;
          color: $color-black;
          padding: 10px;
          transition: 0.3s;

          &:hover {
            color: $color-accent;
          }

          &:last-child {
            box-sizing: border-box;
          }
        }

        &:before {
          content: '';
          position: absolute;
          right: 6px;
          top: -6px;
          display: block;
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid $color-white;
        }
      }
    }
  }

  &-message {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 521px;
    min-height: 157px;
    border-radius: 8px;
    background-color: $color-white;
    padding: 50px;
    font-weight: normal;
    font-size: 15px;
    line-height: 18px;
    text-align: center;

    &__cross {
      position: absolute;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      top: 14px;
      right: 11px;
      color: $color-silver;
      font-size: 16px;
      transition: 0.3s;
      cursor: pointer;

      &:hover {
        color: $color-dusty-gray;
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Projects/ListOfProjects/ListOfProjects.vue